import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/ui/Layout';
import Card from '../../components/ui/Card';
import StatusPill from '../../components/ui/StatusPill';
import EmptyState from '../../components/ui/EmptyState';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton';
import PageHeader from '../../components/ui/PageHeader';
import Button from '../../components/ui/Button';
import { useTranslation } from '../../i18n/LanguageContext';
import {
  IconDroplet,
  IconBriefcase,
  IconSpeakerphone,
  IconPlus,
  IconAlertTriangle,
  IconArrowRight,
  IconBell,
  IconInfoCircle,
} from '@tabler/icons-react';

export default function Operations() {
  const { language } = useTranslation();
  const { orgId, userProfile, role } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('fuel');
  const [tanks, setTanks] = useState([]);
  const [sites, setSites] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);

  const siteId = userProfile?.siteId;

  useEffect(() => {
    const load = async () => {
      if (!orgId) return;
      try {
        const [tanksSnap, sitesSnap, jobsSnap, announcementsSnap] = await Promise.all([
          getDocs(collection(db, 'organisations', orgId, 'dervTanks')),
          getDocs(collection(db, 'organisations', orgId, 'sites')),
          getDocs(query(collection(db, 'organisations', orgId, 'jobs'), orderBy('createdAt', 'desc'))),
          getDocs(query(collection(db, 'organisations', orgId, 'announcements'), orderBy('sentAt', 'desc'))),
        ]);
        let allTanks = tanksSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        if (role === 'manager' && siteId) allTanks = allTanks.filter(t => t.siteId === siteId);
        setTanks(allTanks);
        setSites(sitesSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setJobs(jobsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setAnnouncements(announcementsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    load();
  }, [orgId, siteId, role]);

  const getPct = (tank) => {
    if (!tank.capacityLitres) return 0;
    return Math.round((tank.currentLitres / tank.capacityLitres) * 100);
  };

  const getPctColour = (pct, threshold) => {
    if (pct <= threshold) return 'var(--color-danger)';
    if (pct <= threshold * 1.5) return 'var(--color-warning)';
    return 'var(--color-success)';
  };

  const lowTanks = tanks.filter(t => {
    if (!t.capacityLitres) return false;
    const pct = (t.currentLitres / t.capacityLitres) * 100;
    return pct <= (t.lowLevelThreshold || 20);
  }).length;

  const pendingJobs = jobs.filter(j => j.status === 'pending').length;
  const estimatesAwaiting = jobs.filter(j => j.status === 'estimate-sent').length;

  const formatDateTime = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (d.toDateString() === today.toDateString()) return 'Today · ' + d.toLocaleTimeString(language, { hour: '2-digit', minute: '2-digit' });
    if (d.toDateString() === yesterday.toDateString()) return 'Yesterday · ' + d.toLocaleTimeString(language, { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString(language, { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatDate = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString(language, { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const priorityIcon = (priority) => {
    if (priority === 'urgent') return <IconAlertTriangle size={16} color="var(--color-danger)" stroke={1.5} />;
    if (priority === 'important') return <IconBell size={16} color="var(--color-warning)" stroke={1.5} />;
    return <IconInfoCircle size={16} color="var(--color-brand)" stroke={1.5} />;
  };

  const TABS = [
    { key: 'fuel',          label: 'Fuel',          icon: IconDroplet,      count: tanks.length,         alert: lowTanks       },
    { key: 'jobs',          label: 'Jobs',          icon: IconBriefcase,    count: jobs.length,          alert: estimatesAwaiting },
    { key: 'announcements', label: 'Announcements', icon: IconSpeakerphone, count: announcements.length, alert: 0              },
  ];

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)' }}>
        <PageHeader
          title="Operations"
          subtitle={tanks.length + ' tanks · ' + pendingJobs + ' pending jobs · ' + announcements.length + ' announcements'}
          actions={
            activeTab === 'fuel' ? (
              <Button variant="primary" icon={<IconPlus size={14} stroke={1.5} />} onClick={() => navigate('/fuel/new')}>
                Add tank
              </Button>
            ) : activeTab === 'jobs' ? (
              <Button variant="primary" icon={<IconPlus size={14} stroke={1.5} />} onClick={() => navigate('/jobs/new')}>
                New job request
              </Button>
            ) : activeTab === 'announcements' && role === 'org_admin' ? (
              <Button variant="primary" icon={<IconPlus size={14} stroke={1.5} />} onClick={() => navigate('/announcements/new')}>
                Send announcement
              </Button>
            ) : null
          }
        />

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 2, marginBottom: 20, borderBottom: '0.5px solid var(--color-border-light)' }}>
          {TABS.map(tab => {
            const Icon = tab.icon;
            const active = activeTab === tab.key;
            return (
              <div
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 7,
                  padding: '10px 16px', fontSize: 13, cursor: 'pointer',
                  color: active ? 'var(--color-brand)' : 'var(--color-text-secondary)',
                  borderBottom: active ? '2px solid var(--color-brand)' : '2px solid transparent',
                  marginBottom: -1, transition: 'all 0.15s',
                }}
              >
                <Icon size={14} stroke={1.5} />
                {tab.label}
                <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{tab.count}</span>
                {tab.alert > 0 && (
                  <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 20, background: 'var(--color-danger)', color: '#fff' }}>{tab.alert}</span>
                )}
              </div>
            );
          })}
        </div>

        {/* FUEL TAB */}
        {activeTab === 'fuel' && (
          <div>
            {lowTanks > 0 && (
              <div style={{ background: 'var(--color-danger-light)', border: '0.5px solid var(--color-danger)', borderRadius: 'var(--radius-lg)', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <IconAlertTriangle size={16} color="var(--color-danger)" stroke={1.5} />
                <div style={{ fontSize: 13, color: 'var(--color-danger)' }}>
                  {lowTanks} tank{lowTanks !== 1 ? 's' : ''} running low — order more derv
                </div>
              </div>
            )}

            {loading ? <LoadingSkeleton rows={2} card /> : tanks.length === 0 ? (
              <Card>
                <EmptyState icon={<IconDroplet size={32} stroke={1.5} />} title="No tanks yet" message="Add your first derv tank to start tracking fuel" />
              </Card>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
                {tanks.map(tank => {
                  const pct = getPct(tank);
                  const colour = getPctColour(pct, tank.lowLevelThreshold || 20);
                  return (
                    <Card key={tank.id} onClick={() => navigate('/fuel/' + tank.id)} hover style={{ border: pct <= (tank.lowLevelThreshold || 20) ? '0.5px solid var(--color-danger)' : '0.5px solid var(--color-border-light)' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 }}>
                        <div>
                          <div style={{ fontSize: 15, fontWeight: 400, color: 'var(--color-text-primary)', marginBottom: 2 }}>{tank.name}</div>
                          <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>{tank.siteName || '—'}</div>
                        </div>
                        <div style={{ fontSize: 24, fontWeight: 400, color: colour }}>{pct}%</div>
                      </div>
                      <div style={{ height: 6, background: 'var(--color-bg-tertiary)', borderRadius: 3, overflow: 'hidden', marginBottom: 10 }}>
                        <div style={{ height: '100%', width: pct + '%', background: colour, borderRadius: 3, transition: 'width 0.3s' }} />
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>
                        {tank.currentLitres?.toLocaleString() || 0}L of {tank.capacityLitres?.toLocaleString() || 0}L
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* JOBS TAB */}
        {activeTab === 'jobs' && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 16 }}>
              {[
                { label: 'Total', value: jobs.length, colour: 'var(--color-text-primary)' },
                { label: 'Pending', value: pendingJobs, colour: pendingJobs > 0 ? 'var(--color-warning)' : 'var(--color-text-tertiary)' },
                { label: 'In progress', value: jobs.filter(j => j.status === 'in-progress').length, colour: 'var(--color-brand)' },
                { label: 'Completed', value: jobs.filter(j => j.status === 'completed').length, colour: 'var(--color-success)' },
              ].map(s => (
                <div key={s.label} style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '14px 16px' }}>
                  <div style={{ fontSize: 24, fontWeight: 400, color: s.colour, marginBottom: 4 }}>{s.value}</div>
                  <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{s.label}</div>
                </div>
              ))}
            </div>
            <Card padding="0">
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr 1fr 0.8fr 60px', padding: '8px 16px', borderBottom: '0.5px solid var(--color-border-light)', background: 'var(--color-bg-secondary)' }}>
                {['Machine', 'Description', 'Date', 'Status', ''].map(h => (
                  <div key={h} style={{ fontSize: 10, color: 'var(--color-text-tertiary)', letterSpacing: '0.04em' }}>{h}</div>
                ))}
              </div>
              {loading ? <LoadingSkeleton rows={4} /> : jobs.length === 0 ? (
                <EmptyState icon={<IconBriefcase size={32} stroke={1.5} />} title="No job requests" message="Raise your first job request to your dealer" />
              ) : jobs.map((job, i) => (
                <div key={job.id} onClick={() => navigate('/jobs/' + job.id)} style={{
                  display: 'grid', gridTemplateColumns: '2fr 1.5fr 1fr 0.8fr 60px',
                  padding: '12px 16px', borderBottom: i < jobs.length - 1 ? '0.5px solid var(--color-border-light)' : 'none',
                  cursor: 'pointer', alignItems: 'center',
                }}>
                  <div>
                    <div style={{ fontSize: 13, color: 'var(--color-text-primary)' }}>{job.machineName || '—'}</div>
                    <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{job.machineReg || '—'}</div>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{job.description || '—'}</div>
                  <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{formatDate(job.createdAt)}</div>
                  <StatusPill status={job.status || 'pending'} />
                  <div style={{ fontSize: 12, color: 'var(--color-brand)', textAlign: 'right' }}>View</div>
                </div>
              ))}
            </Card>
          </div>
        )}

        {/* ANNOUNCEMENTS TAB */}
        {activeTab === 'announcements' && (
          <div>
            {loading ? <LoadingSkeleton rows={3} card /> : announcements.length === 0 ? (
              <Card>
                <EmptyState
                  icon={<IconSpeakerphone size={32} stroke={1.5} />}
                  title="No announcements yet"
                  message={role === 'org_admin' ? 'Send your first announcement to your team' : 'No announcements from management yet'}
                />
              </Card>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {announcements.map(a => {
                  const colour = a.priority === 'urgent' ? 'var(--color-danger)' : a.priority === 'important' ? 'var(--color-warning)' : 'var(--color-brand)';
                  return (
                    <Card key={a.id} onClick={() => navigate('/announcements/' + a.id)} hover>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                        <div style={{ flexShrink: 0, marginTop: 2 }}>{priorityIcon(a.priority)}</div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                            <div style={{ fontSize: 14, fontWeight: 400, color: 'var(--color-text-primary)' }}>{a.title}</div>
                            <span style={{
                              fontSize: 10, padding: '1px 8px', borderRadius: 20,
                              background: colour, color: '#fff', marginLeft: 'auto', textTransform: 'capitalize',
                            }}>{a.priority || 'info'}</span>
                          </div>
                          <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.5, marginBottom: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.message}</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 11, color: 'var(--color-text-tertiary)' }}>
                            <span>{a.sentBy}</span>
                            <span>{formatDateTime(a.sentAt)}</span>
                          </div>
                        </div>
                        <IconArrowRight size={15} color="var(--color-text-tertiary)" stroke={1.5} />
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}

      </div>
    </Layout>
  );
}